import React, { useContext, useEffect, useState } from 'react';
import { InspectionStandard } from '../../../../common/src/domain/inspectionStandard/inspectionStandardTableSchema';
import { ObjectType } from '../../../../common/src/domain/objectType/objectTypeTableSchema';
import { useTranslate } from '../../mixins/translate/translate';
import { fetchGet } from '../../store/fetch';
import { EditPanelCell, EditPanelRow, EditPanelSection, EditPanelSelect } from '../elements';
import { ValidatingFilterDropdown, ValidatingFormContext } from '../validatingElements';

interface IInspectionStandardSelectProps {
    objectTypeId: string | undefined;
    inspectionStandardId: string | undefined;
    onObjectTypeIdChange: (id: string) => void;
    onInspectionStandardIdChange: (id: string) => void;
}

export function InspectionStandardSelect({
    objectTypeId,
    inspectionStandardId,
    onObjectTypeIdChange,
    onInspectionStandardIdChange,
}: IInspectionStandardSelectProps) {
    const t = useTranslate();

    const { isLoading, objectTypes, inspectionStandards } = useInspectionStandardSelect(
        objectTypeId,
        inspectionStandardId,
        onObjectTypeIdChange,
        onInspectionStandardIdChange,
    );

    return (
        <>
            <EditPanelSection>
                <EditPanelSelect
                    name="objectTypeId"
                    value={objectTypeId}
                    handleChange={onObjectTypeIdChange}
                    disabled={isLoading}
                    topLabel={t('common.objectType')}
                    placeholder={t('common.selectItem', t('common.objectType'))}
                    options={objectTypes.map((type) => ({ value: type.id, text: type.name }))}
                />
            </EditPanelSection>

            <EditPanelSection>
                <EditPanelSelect
                    name="inspectionStandardId"
                    value={inspectionStandardId}
                    handleChange={onInspectionStandardIdChange}
                    disabled={isLoading || !objectTypeId}
                    topLabel={t('common.inspectionStandard')}
                    placeholder={t('common.selectItem', t('common.inspectionStandard'))}
                    options={inspectionStandards.map((type) => ({ value: type.id, text: type.name }))}
                />
            </EditPanelSection>
        </>
    );
}

export function EditInspectionStandardSelect() {
    const t = useTranslate();
    const formContext = useContext(ValidatingFormContext.context);
    const objectTypeId = (formContext.item.objectTypeId ?? '') as string;
    const inspectionStandardId = (formContext.item.inspectionStandardId ?? '') as string;

    const { objectTypes, inspectionStandards } = useInspectionStandardSelect(
        objectTypeId,
        inspectionStandardId,
        (id: string) => {
            void formContext.onChange('objectTypeId', id);
        },
        (id: string) => {
            void formContext.onChange('inspectionStandardId', id);
        },
    );

    return (
        <EditPanelRow>
            <EditPanelCell rowCount={6}>
                <ValidatingFilterDropdown
                    selectOptions={objectTypes.map((type) => ({ text: type.name, value: type.id }))}
                    topLabel={t('common.objectType')}
                    name="objectTypeId"
                />
            </EditPanelCell>

            <EditPanelCell rowCount={6}>
                <ValidatingFilterDropdown
                    disabled={!objectTypeId}
                    selectOptions={inspectionStandards.map((standard) => ({ text: standard.name, value: standard.id }))}
                    topLabel={t('common.inspectionStandard')}
                    name="inspectionStandardId"
                />
            </EditPanelCell>
        </EditPanelRow>
    );
}

function useInspectionStandardSelect(
    objectTypeId: string | undefined,
    inspectionStandardId: string | undefined,
    onObjectTypeIdChange: (id: string) => void,
    onInspectionStandardIdChange: (id: string) => void,
) {
    const [isLoading, setIsLoading] = useState(true);
    const [objectTypes, setObjectTypes] = useState<ObjectType[]>([]);
    const [inspectionStandards, setInspectionStandards] = useState<InspectionStandard[]>([]);

    useEffect(() => {
        const init = async () => {
            setIsLoading(true);

            const { json: types } = (await fetchGet('/api/objectTypesList', {}, [], [])) as { json: ObjectType[] };
            const { json: standards } = (await fetchGet('/api/inspectionstandards', {}, [], [])) as {
                json: InspectionStandard[];
            };

            if (inspectionStandardId && !objectTypeId) {
                const standard = standards.find((s) => s.id === inspectionStandardId);
                const typeId = standard?.objectTypeId;

                if (typeId) {
                    onObjectTypeIdChange(typeId);
                }
            }

            setObjectTypes(types);
            setInspectionStandards(standards);
        };

        void init().finally(() => setIsLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!objectTypeId) {
            return;
        }

        const standard = inspectionStandards.find((i) => i.id === inspectionStandardId);

        if (!standard || standard.objectTypeId !== objectTypeId) {
            onInspectionStandardIdChange(null as never);
        }
    }, [objectTypeId, inspectionStandardId, inspectionStandards]); // eslint-disable-line react-hooks/exhaustive-deps

    return { isLoading, objectTypes, inspectionStandards: inspectionStandards.filter((standard) => standard.objectTypeId === objectTypeId) };
}
